<template>
  <div
    id="bynder-metadata-modal"
    ref="modal"
    class="modal fade"
    data-backdrop="static"
    tabindex="-1"
    role="dialog"
    aria-labelledby="bynderMetadataModalLabel"
    aria-hidden="true"
  >
    <div
      class="modal-dialog modal-lg"
      role="document"
    >
      <div class="modal-content">
        <div class="modal-header">
          <h5
            id="bynderMetadataModalLabel"
            class="modal-title"
          >
            Bynder Metadata
          </h5>
          <button
            type="button"
            class="close"
            data-dismiss="modal"
            aria-label="Close"
            @click="closeHandler"
          >
            <span aria-hidden="true">&times;</span>
          </button>
        </div>
        <div class="modal-body">
          <div
            v-if="isLoading"
            class="text-center"
          >
            <p>Loading metaproperties...</p>
            <b-spinner variant="primary" />
          </div>

          <div
            v-else-if="error"
            class="alert alert-danger"
          >
            {{ error }}
          </div>

          <div v-else>
            <!-- Text Inputs -->
            <div class="border rounded row p-2 m-2">
              <div
                v-for="prop in metaproperties.longtext"
                :key="prop.id"
                class="col-md-6 mt-2 mb-2"
                :class="{ 'd-none': !prop.visible }"
              >
                <label :for="`longtext-${prop.id}`">{{ prop.label }}</label>
                <input
                  :id="`longtext-${prop.id}`"
                  v-model="form[prop.id].value"
                  type="text"
                  class="form-control"
                  :placeholder="`Enter ${prop.label}`"
                  :disabled="!prop.enabled"
                  @input="updatePropValue(prop.id, $event.target.value)"
                >
              </div>
            </div>

            <!-- Date Inputs -->
            <div class="border rounded row p-2 m-2">
              <div
                v-for="prop in metaproperties.date"
                :key="prop.id"
                class="col-md-6 mt-2 mb-2"
              >
                <label :for="`date-${prop.id}`">{{ prop.label }}</label>
                <input
                  :id="`date-${prop.id}`"
                  v-model="form[prop.id].value"
                  type="date"
                  class="form-control"
                  @input="updatePropValue(prop.id, $event.target.value)"
                >
              </div>
            </div>

            <!-- Selects -->
            <div class="border rounded row p-2 m-2">
              <div
                v-for="prop in metaproperties.select"
                :key="prop.id"
                class="col-md-6 mt-2 mb-2"
              >
                <label :for="`select-${prop.id}`">{{ prop.label }}</label>
                <Multiselect
                  :id="`multiselect-${prop.id}`"
                  :value="findOptionById(prop.id)"
                  :options="prop.options"
                  :multiple="false"
                  track-by="id"
                  label="label"
                  placeholder="Select options"
                  @input="onSelect($event, prop.id)"
                />
              </div>
            </div>

            <!-- Multiselects -->
            <div class="border rounded row p-2 m-2">
              <div
                v-for="prop in metaproperties.multiselect"
                :key="prop.id"
                class="col-md-6 mt-2 mb-2"
              >
                <label :for="`multiselect-${prop.id}`">{{ prop.label }}</label>
                <Multiselect
                  :id="`multiselect-${prop.id}`"
                  :value="parseSelectedIds(prop.id)"
                  :options="prop.options"
                  :multiple="true"
                  :taggable="true"
                  track-by="id"
                  label="label"
                  placeholder="Select options"
                  @input="onMultiSelect($event, prop.id)"
                  @tag="addNewTag($event, prop.id)"
                />
              </div>
            </div>

            <!-- Failed Properties -->
            <div
              v-if="metaproperties.failed.length"
              class="alert alert-info mt-3"
            >
              <div class="text-center">
                <h4 class="alert-heading">
                  Unable to Load Some Properties
                </h4>
                <p>We encountered issues retrieving the following properties:</p>
              </div>
              <ul class="list-unstyled">
                <li
                  v-for="failed in metaproperties.failed"
                  :key="failed.id"
                  class="mb-2"
                >
                  <strong>{{ failed.name }}</strong>
                  <br>
                  <small class="text-muted">
                    ID: {{ failed.id }}
                  </small>
                  <br>
                  <span class="text-danger">
                    Error:
                    {{ failed.message.message || 'Unable to retrieve property' }}
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div class="modal-footer justify-content-between">
          <div>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              :disabled="isLoading"
              @click="saveMetaValues('clear')"
            >
              Clear Selection
            </button>
            <!-- <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="isLoading"
              @click="saveMetaValues('save')"
            >
              Save Selection
            </button> -->
          </div>
          <div>
            <button
              type="button"
              class="btn btn-secondary btn-sm"
              data-dismiss="modal"
              @click="closeHandler"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary btn-sm"
              :disabled="isLoading"
              @click="handleMetaproperties"
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  BYNDER_EVENTS,
  bynderEventBroker,
} from '@frontend/group/modules/bynder/event-broker';
import Multiselect from 'vue-multiselect';
import {
  storePublishPreferences,
  getPublishPreferences,
} from '@frontend/group/modules/publish-utils/publish-utils';
import * as bynder from '@frontend/constants/bynder';
import toastr from 'toastr';
import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'BynderMetadataModal',
  components: {
    Multiselect
  },
  data: () => ({
    metaproperties: {
      longtext: [],
      date: [],
      select: [],
      multiselect: [],
      failed: [],
    },
    form: {},
    isLoading: false,
    error: null,
    saveSelections: false,
    allProps: {},
    imageTypeId: null,
    platformId: null,
    platformKey: null,
    isRegularPublish: true,
    assetType: null,
  }),
  computed: {
    ...mapGetters('bynder', ['isSingleMode']), /* why is it here? */
    ...mapGetters('layout', ['selectedInstance']),
  },
  mounted() {
    bynderEventBroker.on(BYNDER_EVENTS.INIT, () => {
      this.init();

      this.fetchMetaproperties();
    });

    bynderEventBroker.on(BYNDER_EVENTS.PUBLISH_INIT, ({ platformId, platformKey, assetType }) => {
      this.$refs.modal.addEventListener('focusin', (e) => e.stopPropagation());
      $('#bynder-metadata-modal').modal({ backdrop: 'static', keyboard: false, focus: false });

      this.isRegularPublish = false;
      this.platformId = platformId;
      this.platformKey = platformKey;
      this.assetType = assetType;

      this.init();

      this.fetchMetaproperties();
    });
  },
  methods: {
    init() {
      $('#bynder-metadata-modal').modal('show');

      if (!this.platformId && !this.platformKey) {
        const $selectedPlatform = $('#deployment_platform').find(':selected');
        this.platformId = $selectedPlatform.val();
        this.platformKey = $selectedPlatform.data('is-prod') ? bynder.PLATFORM_KEY_PRODUCTION : bynder.PLATFORM_KEY_TESTING;
      }
    },

    closeHandler() {
      this.platformId = null;
      this.platformKey = null;

      $('#bynder-metadata-modal').modal('hide');
    },

    async handleMetaproperties() {
      bynderEventBroker.fire(BYNDER_EVENTS.HANDLE_META, {
        meta: this.form
      });

      if ($('#remember-bynder-settings').is(':checked') && this.isRegularPublish) {
        const filteredForm = this.filteredForm(this.form);

        await storePublishPreferences(bynder.BYNDER_META_VALUES + this.platformKey, filteredForm, true);

        toastr.success('Saved');
      }

      this.closeHandler();
    },

    onSelect(value, id) {
      this.$set(this.form[id], 'value', value ? value.id : '');
      this.$set(this.form[id], 'prop_value', value ? value.label : '');
    },

    findOptionById(propId) {
      const selectedId = this.form[propId].value;

      if (!selectedId) {
        return null;
      };

      const prop = this.metaproperties.select.find((p) => p.id === propId);

      return prop ? prop.options.find((opt) => opt.id === selectedId) : null;
    },

    onMultiSelect(values, propId) {
      const ids = values ? values.map(option => option.id).join(',') : '';
      const labels = values ? values.map(option => option.label).join(', ') : '';

      this.$set(this.form[propId], 'value', ids);
      this.$set(this.form[propId], 'prop_value', labels);
    },

    parseSelectedIds(propId) {
      const formProp = this.form[propId];
      const idsString = formProp?.value ?? '';

      if (!idsString) {
        return [];
      };

      const ids = typeof idsString === 'string' ? idsString.split(',') : [];
      const prop = this.metaproperties.multiselect.find(p => p.id === propId);

      if (!prop) {
        return [];
      };

      return ids
        .map(id => {
          const option = prop.options?.find(opt => opt.id === id);
          if (!option) {
            return {
              id,
              label: id,
              name: id,
            };
          }
          return option;
        })
        .filter(option => option);
    },

    loadSavedData(savedData) {
      Object.entries(savedData).forEach(([propId, data]) => {
        const prop = this.allProps.find(p => p.id === propId);

        if (prop?.default_value === null) {
          let value = null;
          let propValue = '';

          if (prop.name === bynder.PEPSI_ASSET_FORMAT && data.value !== this.imageTypeId) {
            value = this.imageTypeId;
            const matchingOption = prop.options.find(opt => opt.id === this.imageTypeId);
            propValue = matchingOption ? matchingOption.label : '';
          } else {
            value = data.value ?? '';

            if (prop.alt_type === 'select' || prop.alt_type === 'multiselect') {
              const matchingOption = prop.options.find(opt => opt.id === value);
              propValue = matchingOption ? matchingOption.label : '';
            } else {
              propValue = value;
            }
          }

          this.$set(this.form, propId, {
            value: value,
            name: data.name ?? prop.name,
            is_remembered: data.is_remembered ?? prop.is_remembered,
            prop_value: propValue,
          });
        }
      });
    },

    async fetchMetaproperties() {
      this.isLoading = true;
      this.error = null;
      this.metaproperties = { longtext: [], date: [], select: [], multiselect: [], failed: [] };
      this.form = {};

      try {
        const { data } = await axios.post('/bynder-metadata/fetch-metaproperties', {
          platform_id: this.platformId,
        });

        if (data.status === 'error') {
          throw new Error(data.message);
        }

        const rawMetaproperties = data.metaproperties;

        const filteredProps = (props) => props.filter(({ is_item_specific }) => !is_item_specific);

        this.metaproperties = {
          longtext: filteredProps(rawMetaproperties.longtext),
          date: filteredProps(rawMetaproperties.date),
          select: filteredProps(rawMetaproperties.select),
          multiselect: filteredProps(rawMetaproperties.multiselect),
          failed: rawMetaproperties.failed,
        };

        this.initializeForm();

        const savedMetadata = await getPublishPreferences(bynder.BYNDER_META_VALUES + this.platformKey, true);

        if (savedMetadata) {
          this.loadSavedData(savedMetadata);
        }
      } catch (err) {
        this.error = err.message ?? 'Failed to fetch metaproperties';
      } finally {
        this.isLoading = false;
      }
    },

    initializeForm() {
      this.allProps = [
        ...this.metaproperties.longtext,
        ...this.metaproperties.date,
        ...this.metaproperties.select,
        ...this.metaproperties.multiselect,
      ];

      this.allProps.forEach((prop) => {
        const isMultiSelect = prop.alt_type === 'multiselect';

        let defaultValue = isMultiSelect
          ? prop.default_value ?? []
          : prop.default_value ?? '';

        let propValue = '';

        if (prop.name === bynder.PEPSI_ASSET_FORMAT) {
          let selectedValue = null;

          if (this.isRegularPublish) {
            selectedValue = $('#bynder-image-type').find(':selected').val();
            selectedValue = selectedValue === 'jpeg' ? 'jpg' : selectedValue;
          } else {
            selectedValue = this.assetType;
          }

          const matchingOption = prop.options.find(
            option =>
              option.id === selectedValue ||
              option.name === selectedValue ||
              option.label === selectedValue
          );

          defaultValue = matchingOption.id;
          this.imageTypeId = matchingOption.id;
          propValue = matchingOption.label;
        } else {
          if (prop.alt_type === 'select' || prop.alt_type === 'multiselect') {
            const matchingOption = prop.options.find(opt => opt.id === defaultValue);
            propValue = matchingOption ? matchingOption.label : '';
          } else {
            propValue = defaultValue;
          }
        }

        this.$set(this.form, prop.id, {
          value: defaultValue,
          name: prop.name,
          is_remembered: prop.is_remembered,
          prop_value: propValue,
        });
      });
    },

    async saveMetaValues(type = 'save') {
      if (type === 'save') {
        const filteredForm = this.filteredForm(this.form);

        await storePublishPreferences(bynder.BYNDER_META_VALUES + this.platformKey, filteredForm, true);

        toastr.success('Saved');
      } else if (type === 'clear') {
        await storePublishPreferences(bynder.BYNDER_META_VALUES + this.platformKey, null, true);

        this.initializeForm();

        toastr.success('Cleared');
      }
    },

    filteredForm(form) {
      return Object.fromEntries(
        Object.entries(form).filter(([_, propData]) => propData.is_remembered)
      )
    },

    formatDate(dateTime) {
      if (dateTime) {
        return dateTime.split(' ')[0];
      } else {
        return '';
      }
    },

    addNewTag(newTag, propId) {
      const prop = this.metaproperties.multiselect.find(p => p.id === propId);

      if (!prop) {
        return;
      };

      const newOption = {
        id: newTag,
        label: newTag,
        name: newTag,
      };

      if (!prop.options) {
        prop.options = [];
      }

      prop.options.push(newOption);

      const currentValues = this.parseSelectedIds(propId);

      const updatedValues = [...currentValues, newOption];

      const idsString = updatedValues.map(item => item.id).join(',');

      this.$set(this.form[propId], 'value', idsString);
    },

    updatePropValue(propId, value) {
      this.$set(this.form[propId], 'prop_value', value);
    },
  },
};
</script>
